import { useMemo, useState } from 'react';
import { formatContactName, getGradeLabel } from '@car-pool/shared/schoolRecords';
import { apiFetch } from '../config/api';
import SchoolAvatar from './SchoolAvatar';

/**
 * The children this person is authorized to collect, and the controls to change
 * that.
 *
 * Everything here operates on `temporary_authorized_drivers` — the driver-to-
 * child authorization. It never writes to the student's guardians or to a
 * parent's `children_ids`: a family's own relationship to their child is not
 * this screen's to edit, and a row that comes from one is shown read-only with
 * a note saying where it is managed instead.
 */

function todayKey(offsetDays = 0) {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

function formatDate(value) {
  if (!value) return '—';
  const normalized = String(value).slice(0, 10);
  const date = new Date(`${normalized}T12:00:00`);
  if (Number.isNaN(date.getTime())) return normalized;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

function period(record) {
  return `${formatDate(record?.start_date)} – ${formatDate(record?.end_date)}`;
}

function studentName(row) {
  return row.student
    ? `${row.student.f_name} ${row.student.l_name}`.trim()
    : `Student #${row.studentId}`;
}

function authorizedBy(record, parentById) {
  if (!record) return null;
  const parent = parentById?.get(Number(record.authorizing_contact_id));
  if (parent) return `Authorized by ${formatContactName(parent)}`;
  return record.authorizing_staff_id ? 'Added by the school office' : 'Authorized by the school';
}

export default function DriverAuthorizedChildren({
  token,
  schoolId,
  driver,
  rows = [],
  students = [],
  parentById,
  onChanged,
  onOpenStudent,
}) {
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editDates, setEditDates] = useState({ start_date: '', end_date: '' });
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({ student_id: '', start_date: todayKey(0), end_date: todayKey(0) });
  const [addQuery, setAddQuery] = useState('');

  const liveRows = rows.filter((row) => row.live);
  const pastRows = rows.filter((row) => !row.live);

  // A child already covered by a live authorization is not offered again; the
  // existing record's dates are the thing to change.
  const authorizedStudentIds = useMemo(
    () => new Set(liveRows.map((row) => Number(row.studentId))),
    [liveRows]
  );
  const addableStudents = useMemo(() => {
    const needle = addQuery.trim().toLowerCase();
    return (students || [])
      .filter((student) => !authorizedStudentIds.has(Number(student.id)))
      .filter((student) => (needle
        ? `${student.f_name} ${student.l_name}`.toLowerCase().includes(needle)
        : true))
      .slice(0, 50);
  }, [addQuery, authorizedStudentIds, students]);

  const run = async (id, action) => {
    setBusyId(id);
    setError('');
    try {
      await action();
      await onChanged();
      return true;
    } catch (err) {
      setError(err.message || 'That change could not be saved.');
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const startEditing = (row) => {
    setEditingId(row.record.id);
    setEditDates({
      start_date: String(row.record.start_date || '').slice(0, 10),
      end_date: String(row.record.end_date || '').slice(0, 10),
    });
    setError('');
  };

  const saveDates = async (row) => {
    if (!editDates.start_date || !editDates.end_date) {
      setError('Both dates are required.');
      return;
    }
    if (editDates.end_date < editDates.start_date) {
      setError('The end date must be on or after the start date.');
      return;
    }
    const saved = await run(row.record.id, () => apiFetch(`/temporary-authorized-drivers/${row.record.id}`, {
      method: 'PUT',
      body: JSON.stringify({ start_date: editDates.start_date, end_date: editDates.end_date }),
    }, token));
    if (saved) setEditingId(null);
  };

  const removeChild = (row) => run(row.record.id, () => apiFetch(
    `/temporary-authorized-drivers/${row.record.id}`,
    { method: 'DELETE' },
    token
  ));

  const addChild = async () => {
    if (!addForm.student_id) {
      setError('Choose a child to authorize.');
      return;
    }
    if (!addForm.start_date || !addForm.end_date) {
      setError('Both dates are required.');
      return;
    }
    if (addForm.end_date < addForm.start_date) {
      setError('The end date must be on or after the start date.');
      return;
    }
    const added = await run('add', () => apiFetch('/temporary-authorized-drivers', {
      method: 'POST',
      body: JSON.stringify({
        school_id: Number(schoolId),
        student_id: Number(addForm.student_id),
        authorized_contact_id: Number(driver.id),
        start_date: addForm.start_date,
        end_date: addForm.end_date,
        // Added at the office on a parent's say-so, so it is effective at once
        // rather than waiting for the driver to answer a request.
        status: 'accepted',
        notes: 'Added by the school office',
      }),
    }, token));
    if (added) {
      setAdding(false);
      setAddForm({ student_id: '', start_date: todayKey(0), end_date: todayKey(0) });
      setAddQuery('');
    }
  };

  return (
    <div className="sap-section-card sad-authorization-history">
      <div className="sap-section-caption sad-authorized-caption">
        <span>Authorized to pick up ({liveRows.length})</span>
        {!adding ? (
          <button type="button" className="sa-btn sa-btn-primary sad-add-child-btn" onClick={() => { setAdding(true); setError(''); }}>
            Add a child
          </button>
        ) : null}
      </div>

      {error ? <div className="sa-error sap-inline-error">{error}</div> : null}

      {adding ? (
        <div className="sad-add-child">
          <label className="sad-add-field">
            <span>Find a child</span>
            <input
              type="search"
              value={addQuery}
              onChange={(event) => setAddQuery(event.target.value)}
              placeholder="Search students by name"
            />
          </label>
          <label className="sad-add-field">
            <span>Child</span>
            <select
              value={addForm.student_id}
              onChange={(event) => setAddForm((prev) => ({ ...prev, student_id: event.target.value }))}
            >
              <option value="">Select a child…</option>
              {addableStudents.map((student) => (
                <option key={student.id} value={student.id}>
                  {student.f_name} {student.l_name} · {getGradeLabel(student.grade, {}, { short: true })}
                </option>
              ))}
            </select>
          </label>
          <div className="sad-add-dates">
            <label className="sad-add-field">
              <span>From</span>
              <input
                type="date"
                value={addForm.start_date}
                onChange={(event) => setAddForm((prev) => ({ ...prev, start_date: event.target.value }))}
              />
            </label>
            <label className="sad-add-field">
              <span>Until</span>
              <input
                type="date"
                value={addForm.end_date}
                onChange={(event) => setAddForm((prev) => ({ ...prev, end_date: event.target.value }))}
              />
            </label>
          </div>
          <div className="sad-add-actions">
            <button type="button" className="sa-btn" onClick={() => { setAdding(false); setError(''); }} disabled={busyId === 'add'}>
              Cancel
            </button>
            <button type="button" className="sa-btn sa-btn-primary" onClick={addChild} disabled={busyId === 'add'}>
              {busyId === 'add' ? 'Authorizing…' : 'Authorize'}
            </button>
          </div>
        </div>
      ) : null}

      {liveRows.length ? liveRows.map((row) => (
        <div className="sad-authorization-record" key={row.record ? `r${row.record.id}` : `s${row.studentId}`}>
          <SchoolAvatar record={row.student} />
          <div className="sad-authorization-main">
            <button type="button" className="sad-authorization-name" onClick={() => onOpenStudent(row.studentId)}>
              <strong>{studentName(row)}</strong>
              {row.student ? <small>{getGradeLabel(row.student.grade, {}, { short: true })}</small> : null}
            </button>

            {row.editable && editingId === row.record.id ? (
              <div className="sad-edit-dates">
                <input
                  type="date"
                  aria-label={`Start date for ${studentName(row)}`}
                  value={editDates.start_date}
                  onChange={(event) => setEditDates((prev) => ({ ...prev, start_date: event.target.value }))}
                />
                <input
                  type="date"
                  aria-label={`End date for ${studentName(row)}`}
                  value={editDates.end_date}
                  onChange={(event) => setEditDates((prev) => ({ ...prev, end_date: event.target.value }))}
                />
                <button type="button" className="sa-btn sa-btn-primary" onClick={() => saveDates(row)} disabled={busyId === row.record.id}>
                  Save
                </button>
                <button type="button" className="sa-btn" onClick={() => setEditingId(null)} disabled={busyId === row.record.id}>
                  Cancel
                </button>
              </div>
            ) : (
              <span className="sad-authorization-meta">
                {row.editable ? period(row.record) : 'Permanent link'}
                {row.editable && authorizedBy(row.record, parentById) ? ` · ${authorizedBy(row.record, parentById)}` : ''}
              </span>
            )}
          </div>

          {row.editable ? (
            editingId === row.record.id ? null : (
              <div className="sad-authorization-actions">
                <span className={`sad-status-badge ${row.record.status || 'inactive'}`}>{row.record.status}</span>
                <button type="button" className="sa-btn" onClick={() => startEditing(row)} disabled={busyId === row.record.id}>
                  Change dates
                </button>
                <button
                  type="button"
                  className="sa-btn sa-btn-danger"
                  onClick={() => removeChild(row)}
                  disabled={busyId === row.record.id}
                >
                  {busyId === row.record.id ? 'Removing…' : 'Remove'}
                </button>
              </div>
            )
          ) : (
            <span className="sad-permanent-note" title="Managed on the student's own record">
              Managed on the student record
            </span>
          )}
        </div>
      )) : (
        <div className="sap-section-empty">
          This account is not authorized to pick up any child yet.
        </div>
      )}

      {pastRows.length ? (
        <details className="sad-past-authorizations">
          <summary>Past authorizations ({pastRows.length})</summary>
          {pastRows.map((row) => (
            <div className="sad-authorization-record sad-authorization-past" key={`p${row.record.id}`}>
              <div className="sad-authorization-main">
                <strong>{studentName(row)}</strong>
                <span className="sad-authorization-meta">{period(row.record)}</span>
              </div>
              <span className={`sad-status-badge ${row.record.status || 'inactive'}`}>{row.record.status}</span>
            </div>
          ))}
        </details>
      ) : null}
    </div>
  );
}
